import React, { useEffect, useState } from "react";
import { Layout, Menu, Avatar, Tooltip, Modal } from "antd";
import type { MenuProps } from "antd";
import {
  LogoutOutlined,
  AccountBookOutlined,
  GlobalOutlined,
  ProjectOutlined,
  TeamOutlined,
  SettingOutlined,
  FileSearchOutlined,
  BarChartOutlined,
  LaptopOutlined,
} from "@ant-design/icons";
import { BiBuilding, BiHome } from "react-icons/bi";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../../core/store/reducers/auth.slice";
import { RootState } from "../../types/common";
import {
  ROUTE_MANAGE_DEPARTMENT,
  ROUTE_STAFF_DASHBOARD,
  ROUTE_MANAGE_STAFF,
  ROUTE_PROJECT_DASHBOARD,
  ROUTE_MANAGE_PROJECT,
  ROUTE_MILESTONES,
  ROUTE_MANAGE_VENDOR,
  ROUTE_BILLING_SUMMARY,
  ROUTE_ALLOCATION_REPORT,
  ROUTE_REPORTS,
  ROUTE_MANAGE_ACCOUNT,
  ROUTE_MANAGE_SUB_DEPARTMENT,
  ROUTE_MANAGE_DEAL_STAGE,
  ROUTE_MANAGE_DEAL_SUB_STAGE,
  ROUTE_MANAGE_FOREX,
  ROUTE_SALES_DASHBOARD,
  ROUTE_MANAGE_SALES,
  ROUTE_SALES_WON,
  ROUTE_SALES_LOST,
  ROUTE_IT_DASHBOARD,
  ROUTE_MANAGE_IT,
  ROUTE_CANDIDATE_SCREENING,
  ROUTE_RESUME_MASTER,
  // ROUTE_SCREENING_RULES,
  ROUTE_SCREENING_AI_CONFIG,
  ROUTE_MANAGE_USERS,
  ROUTE_MANAGE_ROLES,
  ROUTE_MANAGE_EMAIL,
  ROUTE_MANAGE_DOCUMENT_TYPE,
  ROUTE_MANAGE_DOCUMENT_SUB_TYPE,
  ROUTE_DASHBOARD,
  ROUTE_MANAGE_RELEASE,
} from "../../routes/private.routes";
import { allPermissions } from "../../constants/constants";
import { APP_VERSION } from "../../constants";
import { API_APP_VERSION } from "../../routes/auth.routes";
import { Client } from "../../../core/api-client";
import { NavigationBlockerContext } from "../../../App";
import "../../../assets/styles/sidebar.less";
import { useMsal } from "@azure/msal-react";
 
const { Sider } = Layout;
const SIDEBAR_LOGO = "/assets/images/robotico-logo.svg";
 
type MenuItem = Required<MenuProps>["items"][number];
 
function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[],
  permission?: string[],
  popupClassName?: string
): MenuItem {
  return {
    key,
    icon,
    children: children?.length ? children : undefined,
    label,
    permission,
    popupClassName,
  } as MenuItem;
}
 
const SidebarComponent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { instance } = useMsal();
  const { user, myPermissions } = useSelector((state: RootState) => state.auth);
  const [collapsed, setCollapsed] = useState(true);
  const [apiVersion, setApiVersion] = useState("");
  const { blocker } = React.useContext(NavigationBlockerContext);
  const displayName = user?.fullName || "User";
  const displayInitial = displayName.trim().charAt(0).toUpperCase();
  const displayUserName = user?.user_name || "";
  const versionText = `UI v${APP_VERSION} | API v${apiVersion || "--"}`;

  useEffect(() => {
    Client.get(API_APP_VERSION, { shouldHide: true })
      .then((response: any) => {
        setApiVersion(response?.data?.version || "");
      })
      .catch(() => {
        setApiVersion("");
      });
  }, []);

  const userPermissionSet = new Set(
    myPermissions?.map((p: any) => p.permission?.name) || []
  );
  const hasPermission = (perms?: string[]) => {
    if (!perms || perms.length === 0) return true;
    return perms.some((p) => userPermissionSet.has(p));
  };
 
  const menuItems: MenuItem[] = [
    getItem("Home", ROUTE_DASHBOARD, <BiHome />, undefined, [allPermissions.VIEW_DASHBOARD]),
    getItem(
      "Staff",
      "staff",
      <TeamOutlined />,
      [
        getItem(
          "Dashboard",
          ROUTE_STAFF_DASHBOARD,
          null,
          undefined,
          [allPermissions.VIEW_STAFF]
        ),
        getItem("Manage Staff", ROUTE_MANAGE_STAFF, null, undefined, [
          allPermissions.VIEW_STAFF,
        ]),
      ],
      [allPermissions.VIEW_STAFF]
    ),
    getItem(
      "Project",
      "project",
      <ProjectOutlined />,
      [
        getItem("Dashboard", ROUTE_PROJECT_DASHBOARD, undefined, undefined, [
          allPermissions.VIEW_PROJECT,
        ]),
        getItem("Manage Project", ROUTE_MANAGE_PROJECT, undefined, undefined, [allPermissions.VIEW_PROJECT]),
        getItem("Milestones", ROUTE_MILESTONES, undefined, undefined, [allPermissions.MILESTONE_VIEW]),
        getItem("Allocation Report", ROUTE_ALLOCATION_REPORT, undefined, undefined, [allPermissions.ALLOCATION_REPORT_VIEW]),
        getItem("Billing Summary", ROUTE_BILLING_SUMMARY, undefined, undefined, [allPermissions.VIEW_BILLING_SUMMARY]),
      ],
      []
    ),
    getItem(
      "Sales",
      "sales",
      <AccountBookOutlined />,
      [
        getItem("Dashboard", ROUTE_SALES_DASHBOARD, undefined, undefined, [
          allPermissions.VIEW_SALES,
        ]),
        getItem("Pipeline", ROUTE_MANAGE_SALES, undefined, undefined, [
          allPermissions.VIEW_SALES,
        ]),
        getItem("Won", ROUTE_SALES_WON, undefined, undefined, [allPermissions.VIEW_SALES]),
        getItem("Lost", ROUTE_SALES_LOST, undefined, undefined, [allPermissions.VIEW_SALES]),
      ],
      [allPermissions.VIEW_SALES]
    ),
     getItem(
      "Reports",
      "reports",
      <BarChartOutlined />,
      [
        getItem("Manage Reports", ROUTE_REPORTS, undefined, undefined, [allPermissions.VIEW_REPORT]),
      ],
      [allPermissions.VIEW_REPORT]
    ),
    getItem(
      "IT",
      "it",
      <LaptopOutlined />,
      [
        getItem("Dashboard", ROUTE_IT_DASHBOARD, undefined, undefined, [
          allPermissions.VIEW_IT_ASSET,
        ]),
        getItem("Manage IT", ROUTE_MANAGE_IT, undefined, undefined, [
          allPermissions.VIEW_IT_ASSET,
        ]),
      ],
      [allPermissions.VIEW_IT_ASSET]
    ),
     getItem(
      "ATS",
      "candidate-screening",
      <FileSearchOutlined />,
      [
        getItem("JD Master", ROUTE_CANDIDATE_SCREENING, undefined, undefined, [allPermissions.VIEW_JD_MASTER]),
        getItem("Resume Master", ROUTE_RESUME_MASTER, undefined, undefined, [allPermissions.VIEW_RESUME_MASTER]),
        // getItem("Screening Rules", ROUTE_SCREENING_RULES, undefined, undefined),
        getItem("AI Configuration", ROUTE_SCREENING_AI_CONFIG, undefined, undefined, [allPermissions.AICONFIG_VIEW]),
      ],
      [allPermissions.VIEW_JD_MASTER, allPermissions.VIEW_RESUME_MASTER]
    ),
   
    getItem(
      "Settings",
      "Settings",
      <SettingOutlined />,
      [
        getItem("Manage Vendor", ROUTE_MANAGE_VENDOR, undefined, undefined, [allPermissions.VIEW_VENDOR]),
        getItem("Manage Client", ROUTE_MANAGE_ACCOUNT, undefined, undefined, [allPermissions.VIEW_ACCOUNT]),
        getItem("Manage Department", ROUTE_MANAGE_DEPARTMENT, undefined, undefined, [allPermissions.VIEW_DEPARTMENT]),
        getItem("Manage Sub Department", ROUTE_MANAGE_SUB_DEPARTMENT, undefined, undefined, [allPermissions.VIEW_SUBDEPARTMENT]),
        getItem("Manage Deal Stage", ROUTE_MANAGE_DEAL_STAGE, undefined, undefined, [allPermissions.VIEW_DEAL_STAGE]),
        getItem("Manage Deal Sub-Stage", ROUTE_MANAGE_DEAL_SUB_STAGE, undefined, undefined, [allPermissions.VIEW_DEAL_SUB_STAGE]),
        getItem("Manage Forex", ROUTE_MANAGE_FOREX, undefined, undefined, [allPermissions.VIEW_FOREX]),
        getItem("Manage Users", ROUTE_MANAGE_USERS, undefined, undefined, [allPermissions.VIEW_USER]),
        getItem("Manage Roles", ROUTE_MANAGE_ROLES, undefined, undefined, [allPermissions.VIEW_ROLE]),
        getItem("Manage Email", ROUTE_MANAGE_EMAIL, undefined, undefined, [allPermissions.VIEW_EMAIL]),
        getItem("Manage Document Type", ROUTE_MANAGE_DOCUMENT_TYPE, undefined, undefined, [allPermissions.VIEW_DOCUMENT_TYPE]),
        getItem("Manage Document Sub Type", ROUTE_MANAGE_DOCUMENT_SUB_TYPE, undefined, undefined, [allPermissions.VIEW_DOCUMENT_SUB_TYPE]),
        getItem("Manage Release", ROUTE_MANAGE_RELEASE, undefined, undefined, [allPermissions.VIEW_RELEASE]),
      ],
      undefined,
      "sidebar-settings-popup"
    ),
   
  ]
    .filter((item: any) => hasPermission(item.permission)) as MenuItem[];
 
  // Filter children by permission
  const filteredItems = menuItems.map((item: any) => {
    if (item.children) {
      return {
        ...item,
        children: item.children.filter((child: any) =>
          hasPermission(child.permission)
        ),
      };
    }
    return item;
  }).filter((item: any) => !item.children || item.children.length > 0);
 
  const onMenuClick = ({ key }: { key: string }) => {
    if (key && key.startsWith("/")) {
      if (blocker) { // Blocker is now a function or null
        blocker({ pathname: key, search: "" }); // Trigger the blocker
      } else {
        navigate(key);
      }
    }
  };
 
  const handleLogout = () => {
    instance.logoutRedirect({
      onRedirectNavigate: () => {
        // Return false to stop navigation to the MS sign-out page
        return false;
      },
    });
    setTimeout(() => {
      dispatch(logout());
    }, 500);
  };
 
  const showLogoutConfirm = () => {
    Modal.confirm({
      title: "Are you sure you want to logout?",
      okText: "Logout",
      cancelText: "Cancel",
      centered: true,
      okButtonProps: { danger: true },
      onOk: handleLogout,
    });
  };
 
  const selectedKey = location.pathname.startsWith(ROUTE_MANAGE_STAFF)
    ? ROUTE_MANAGE_STAFF
    : location.pathname.startsWith(ROUTE_REPORTS)
    ? ROUTE_REPORTS
    : location.pathname;
 
  const defaultOpenKeys = (() => {
    const path = location.pathname;
    if (path.startsWith("/staff-dashboard")) return ["staff"];
    if (path.startsWith("/manage-staff")) return ["staff"];
    if (path.startsWith("/project-dashboard")) return ["project"];
    if (path.startsWith("/manage-project") || path.startsWith("/milestones") || path.startsWith("/billing-summary")) return ["project"];
    if (path.startsWith("/reports")) return ["reports"];
    if (path.startsWith("/sales-dashboard")) return ["sales"];
    if (path.startsWith("/manage-sales")) return ["sales"];
    if (path.startsWith("/it-dashboard")) return ["it"];
    if (path.startsWith(ROUTE_MANAGE_IT)) return ["it"];
    if (
      path.startsWith("/candidate-screening") ||
      path.startsWith("/candidate-screening/resume-master") ||
      path.startsWith("/screening-results") ||
      path.startsWith("/screening-rules") ||
      path.startsWith("/screening-ai-config")
    )
      return ["candidate-screening"];
    if (
      path.startsWith("/manage-vendor") ||
      path.startsWith("/manage-client") ||
      path.startsWith("/manage-account") ||
      path.startsWith("/manage-department") ||
      path.startsWith("/manage-sub-department") ||
      path.startsWith("/manage-deal-stage") ||
      path.startsWith("/manage-deal-sub-stage") ||
      path.startsWith("/manage-forex") ||
      path.startsWith("/manage-users") ||
      path.startsWith("/manage-roles") ||
      path.startsWith("/manage-document-type") ||
      path.startsWith("/manage-document-sub-type")
    )
      return ["Settings"];
    return [];
  })();
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const handleOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
  };
 
  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={(nextCollapsed) => {
        setCollapsed(nextCollapsed);
        setOpenKeys(nextCollapsed ? [] : defaultOpenKeys);
      }}
      width={260}
      collapsedWidth={72}
      className="app-sidebar"
      theme="dark"
    >
      <div className="sidebar-header">
        <div className="sidebar-logo text-center">
          {!collapsed && <img
            alt="Robotico logo"
            src={SIDEBAR_LOGO}
            className="sidebar-logo-img"
          />}
          {collapsed && <span className="sidebar-logo-text">RD</span>}
        </div>
      </div>
 
      <div className="sidebar-menu-wrap">
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          openKeys={openKeys}
          onOpenChange={handleOpenChange}
          items={filteredItems}
          onClick={onMenuClick}
          className="sidebar-menu"
        />
      </div>
 
      <div className="sidebar-footer">
        <div className="sidebar-profile-card">
          <div className="sidebar-profile-main">
            <Avatar
              size={collapsed ? 36 : 40}
              className="sidebar-profile-avatar"
            >
              {displayInitial}
            </Avatar>
            {!collapsed && (
              <div className="sidebar-profile-info">
                <Tooltip
                  title={
                    <div>
                      <div>{displayName}</div>
                      {displayUserName && <div>{displayUserName}</div>}
                    </div>
                  }
                  placement="top"
                >
                  <span className="sidebar-profile-name">
                    {displayName}
                  </span>
                </Tooltip>
                {displayUserName && (
                  <Tooltip title={displayUserName} placement="top">
                    <span className="sidebar-profile-role">
                      {displayUserName}
                    </span>
                  </Tooltip>
                )}
              </div>
            )}
          </div>
          <Tooltip title={collapsed ? "Logout" : ""} placement="right">
            <button
              className="sidebar-logout-button"
              type="button"
              onClick={showLogoutConfirm}
              aria-label="Logout"
            >
              <LogoutOutlined />
              {!collapsed && <span>Logout</span>}
            </button>
          </Tooltip>
          {!collapsed && <div className="sidebar-version-text">{versionText}</div>}
        </div>
      </div>
    </Sider>
  );
};
 
export default SidebarComponent;
 
 
 
 
